import {
  Suspense, useState, useEffect, useMemo, useRef, useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getContracts } from 'modules/contracts/actions';
import { contractsActions } from 'modules/contracts';
import useLiterals from 'utils/hooks/useLiterals';
import PaymentCheckout from 'components/PaymentCheckout';
import PaymentMethodSelector from 'components/PaymentMethods/PaymentMethodSelector';
import usePaymentMethods, { isMethodSelectable } from 'components/PaymentMethods/hooks/usePaymentMethods';
import { activatePaymentMethod } from 'components/PaymentMethods/activation';
import { LazyBBVA, LazyMercadoPago, LazyPaymentReference } from 'components/PaymentMethods/lazyMethods';
import Box from 'components/UI/Box/Box';
import Select from 'components/UI/Select';
import Button from 'components/UI/Button';
import { formatCurrency } from 'utils/formatCurrency';
import { translate } from 'utils/translate';
import useOutOfOffice from 'utils/hooks/useOutOfOffice';
import PaymentOutOfHour from 'components/PaymentOutOfHour';
import { renderIconCategories } from 'components/PaymentBox';
import Translate from 'components/Translate';
import InputAmountSteps from 'components/UI/InputAmountSteps';
import {
  CONTRACT,
  CONTRACT_STATUS,
  PAYCASH_MAX_AMOUNT,
  PROVIDER_TYPE,
} from 'constants/global';
import { formatContractPaymentOptions } from 'utils/formatContratPaymentOption';
import ContractPaymentOptions from 'routes/ContractPayment/components/ContractPaymentOptions';
import { formatDate } from 'utils/formatDate';
import { mergePaymentOrdersWithEmpaques } from 'utils/paymentSummaryEmpaque';
import PaymentResumeEmpaqueRow from 'routes/ContractPayment/components/PaymentResumeEmpaqueRow';
import { ROUTE_PATH } from 'routes';
import PaymentSummaryBarcode from 'components/PaymentSummaryBarcode';
import {
  resolvePaymentSummaryBarcodeReference,
} from 'utils/paymentBarcodeReference';
import './styles.scss';

const MultiplePayment = () => {
  const literals = useLiterals('contractPay');
  const checkoutLiterals = useLiterals('paymentCheckout');
  const allContracts = useSelector((state) => state.contracts);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { schedule: paymentsSchedule, isOutOfHour } = useOutOfOffice();
  const [form, setForm] = useState({});
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [activation, setActivation] = useState({ status: 'idle' });
  const [step, setStep] = useState(0);
  const [showOptions, setShowOptions] = useState([]);
  const [multiplePaymentSummary, setMultiplePaymentSummary] = useState(null);
  // Latest provider requested, used to ignore stale activation results.
  const latestProviderRef = useRef(null);

  const contracts = useMemo(() => {
    const data = [];
    if (allContracts?.list) {
      allContracts.list.forEach((contract) => {
        if (contract.OpcionesPago.length > 0) {
          data.push({
            ...contract,
            options: formatContractPaymentOptions(contract?.OpcionesPago, literals),
          });
        }
      });
    }
    return data;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allContracts]);

  const { totalAmount, validAmount } = useMemo(() => (
    {
      totalAmount: parseFloat(Object.values(form).reduce((acc, curr) => acc + parseFloat(curr?.amount || 0), 0.00).toFixed(2)),
      validAmount: Object.values(form).every((c) => !c.error),
    }
  ), [form]);
  const paycashAvailable = totalAmount <= PAYCASH_MAX_AMOUNT;
  // The multiple flow never checked the card block; keep that behavior.
  const methods = usePaymentMethods({ cardBlocked: false, amount: totalAmount });

  const steps = useMemo(() => [
    { label: checkoutLiterals.steps.contracts },
    { label: checkoutLiterals.steps.paymentMethod },
    { label: checkoutLiterals.steps.confirmation },
  ], [checkoutLiterals]);

  const checkoutSummary = useMemo(() => {
    const items = Object.entries(form)
      .filter(([, option]) => parseFloat(option?.amount || 0) > 0)
      .map(([contractId, option]) => ({
        id: contractId,
        label: translate(checkoutLiterals.summary.contract, { number: contractId }),
        description: option.title || undefined,
        amount: parseFloat(option.amount),
      }));
    if (items.length === 0) return null;
    return { items, total: totalAmount };
  }, [form, totalAmount, checkoutLiterals]);

  const activateMethod = useCallback((provider) => {
    latestProviderRef.current = provider;
    setActivation({ status: 'loading' });
    activatePaymentMethod(provider)
      .then(() => {
        if (latestProviderRef.current === provider) setActivation({ status: 'ready' });
      })
      .catch(() => {
        if (latestProviderRef.current === provider) setActivation({ status: 'error' });
      });
  }, []);

  const handleSelectMethod = (provider) => {
    setPaymentMethod(provider);
    activateMethod(provider);
  };

  // The first time the user reaches "Forma de pago", PayCash (the recommended method)
  // comes preselected when it is available; any later choice is kept as is.
  const didPreselectRef = useRef(false);
  useEffect(() => {
    if (step !== 1 || didPreselectRef.current) return;
    didPreselectRef.current = true;
    if (!paymentMethod && isMethodSelectable(methods, PROVIDER_TYPE.PAYCASH)) {
      handleSelectMethod(PROVIDER_TYPE.PAYCASH);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const handleRetryActivation = () => {
    if (paymentMethod) activateMethod(paymentMethod);
  };

  useEffect(() => {
    dispatch(getContracts());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickStep0 = () => {
    if (!paycashAvailable && paymentMethod === PROVIDER_TYPE.PAYCASH) {
      setPaymentMethod(null);
      latestProviderRef.current = null;
      setActivation({ status: 'idle' });
    }
    setStep(1);
  };

  const handleSelectPayment = (option, contractId) => {
    const newValue = { ...form };
    if (!option) {
      delete newValue[contractId];
    } else {
      newValue[contractId] = {
        ...option,
        amount: option.amount,
        originalAmount: option.amount,
        error: false,
      };
    }
    setForm(newValue);
  };

  const handleChangeAmount = (value, contractId) => {
    const newValue = { ...form };
    const oldValue = newValue[contractId];

    const error = (oldValue?.min && value < oldValue.min && value !== oldValue?.amount) || (oldValue?.max && value > oldValue.max);

    newValue[contractId] = {
      ...newValue[contractId],
      amount: value,
      error,
    };
    setForm(newValue);
  };

  const renderStep0 = () => {
    return (
      <>
        <div className='contracts-list'>
          {contracts.map((contract) => {
            const selected = form[contract.Numero] || null;
            const articleDescription = contract?.Descripcion?.trim?.() || '';
            return (
              <Box className='contract-item mb-6' key={contract.Numero}>
                <div className='mb-4'>
                  <div className='flex gap-4 items-center mb-2'>
                    {renderIconCategories(contract.Ramo, contract.isAuto)}
                    <div className='contract-item-header-text'>
                      <h3 className='mb-0'>{contract.Numero}</h3>
                      {articleDescription ? (
                        <p className='contract-item-description mb-0'>
                          {articleDescription}
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <p>
                    <b>{`${literals.office}: `}</b>
                    {contract.NombreSucursal}
                  </p>
                  <p className='mb-0'>
                    <b>{`${literals.pendingCapital}: `}</b>
                    {formatCurrency(contract.CapitalRestante)}
                  </p>
                </div>
                {contract.seRealizoPago ? (
                  <Select
                    value='0'
                    options={[{
                      label: literals.onlyOnePayment,
                      value: '0',
                    }]}
                    onChange={() => {}}
                    disabled
                  />
                ) : (
                  <>
                    <Select
                      value={showOptions.includes(contract.Numero) ? '1' : '0'}
                      options={[
                        {
                          label: literals.noPaymentOption,
                          value: '0',
                        },
                        {
                          label: literals.showPaymentOptions,
                          value: '1',
                        },
                      ]}
                      onChange={(v) => {
                        if (v === '1') {
                          setShowOptions([...showOptions, contract.Numero]);
                        } else {
                          setShowOptions(showOptions.filter((c) => c !== contract.Numero));
                          handleSelectPayment(null, contract.Numero);
                        }
                      }}
                      canBeEmpty={false}
                    />
                    {showOptions.includes(contract.Numero) && (
                      <ContractPaymentOptions
                        className='mt-4'
                        label={false}
                        literals={literals}
                        options={contract.options}
                        selectedPayment={selected?.key || '0'}
                        onSelectPayment={(v) => handleSelectPayment(contract.options.find((c) => c.key === v), contract.Numero)}
                      />
                    )}
                  </>
                )}
                {selected && (
                  <>
                    <div className='mt-2 mb-1 font-bold'>{literals.amount}</div>
                    <InputAmountSteps
                      value={selected.amount}
                      originalValue={selected.amount}
                      min={selected.min}
                      max={selected.max}
                      error={selected.error}
                      onChange={(v) => handleChangeAmount(v, contract.Numero)}
                    />
                  </>
                )}
              </Box>
            );
          })}
        </div>
        <Button
          onClick={handleClickStep0}
          text={`${literals.common.next} ${totalAmount ? `(${formatCurrency(totalAmount)})` : ''}`}
          className='mt-4'
          disabled={totalAmount === 0 || !validAmount}
        />
      </>
    );
  };

  const renderStep1 = () => {
    return (
      <>
        <Translate
          className='mb-6 text-neutral-700'
          literal={literals.multipleResume}
          vars={{ contracts: Object.keys(form).length, amount: formatCurrency(totalAmount) }}
        />
        <PaymentMethodSelector
          methods={methods}
          selected={paymentMethod}
          onSelect={handleSelectMethod}
          activation={activation}
          onRetry={handleRetryActivation}
          onPay={() => setStep(2)}
        />
      </>
    );
  };

  const renderStep2 = () => {
    const orders = Object.keys(form).map((cid) => ({
      contract_id: cid,
      payment_key: form[cid].paymentKey,
      contract_amount: form[cid].originalAmount,
      amount: parseFloat(form[cid].amount),
      payment_name: form[cid].title,
    }));

    if (paymentMethod === PROVIDER_TYPE.PAYCASH || paymentMethod === PROVIDER_TYPE.PUNTORED) {
      return (
        <LazyPaymentReference
          provider={paymentMethod}
          amount={totalAmount}
          orders={orders}
          isMultiple
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.BANCOMER) {
      return (
        <LazyBBVA
          orders={orders}
          amount={totalAmount}
          isMultiple
          onClose={() => {
            setStep(1);
          }}
        />
      );
    }
    if (paymentMethod === PROVIDER_TYPE.MERCADO_PAGO) {
      return (
        <LazyMercadoPago
          amount={totalAmount}
          orders={orders}
          isMultiple
          onClose={() => {
            // Volver a la selección de método de pago (step 1), no quedarse en
            // el paso "Realizar pago" mostrando "Próximamente" (WEB_MLS-532).
            setStep(1);
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS_MULTIPLE, { replace: true });
          }}
          onSuccess={(response) => {
            dispatch(contractsActions.needUpdate());
            const responseData = response?.data ?? response;
            const paymentDate = formatDate(new Date(), 'DD/MM/YYYY');
            const barcodeReference = resolvePaymentSummaryBarcodeReference({
              payment: responseData,
              type: CONTRACT,
              date: paymentDate,
              context: 'response',
            });
            setMultiplePaymentSummary({
              orders: mergePaymentOrdersWithEmpaques(orders, response),
              totalAmount,
              date: new Date(),
              providerType: PROVIDER_TYPE.MERCADO_PAGO,
              status: CONTRACT_STATUS.COMPLETED,
              ...(barcodeReference != null ? { barcodeReference } : {}),
            });
          }}
          onPending={(pendingResult) => {
            setMultiplePaymentSummary({
              orders: mergePaymentOrdersWithEmpaques(orders, pendingResult),
              totalAmount,
              date: new Date(),
              providerType: PROVIDER_TYPE.MERCADO_PAGO,
              status: CONTRACT_STATUS.PENDING,
              warningMessage: pendingResult?.message,
              mpStatus: pendingResult?.status,
              mpStatusDetail: pendingResult?.statusDetail,
              mpPaymentId: pendingResult?.paymentId,
            });
          }}
        />
      );
    }
    return <h1>Próximamente</h1>;
  };

  const handleBack = () => {
    if (multiplePaymentSummary) {
      setMultiplePaymentSummary(null);
      setStep(0);
      setPaymentMethod(null);
      navigate(ROUTE_PATH.PAYMENTS);
      return;
    }
    if (step > 0) {
      setStep(step - 1);
      return;
    }
    navigate(-1);
  };

  const renderMultiplePaymentSummary = () => {
    const summary = multiplePaymentSummary;
    if (!summary) return null;
    return (
      <div className='payment-resume'>
        <h1 className='mb-6 text-primary'>{literals.paymetResume}</h1>
        {summary.warningMessage && (
          <div className='alert alert-warning mb-4' role='alert'>
            <strong>{literals.warningTitle || 'Atención'}</strong>
            {' '}
            {summary.warningMessage}
          </div>
        )}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.contracts}</h2>
          <h3 className='text-neutral-500'>{summary.orders?.length || 0}</h3>
        </div>
        {(summary.orders || []).map((order) => (
          <div key={order.contract_id} className='payment-resume-contract-info mb-2'>
            <div className='d-flex justify-content-between'>
              <h2 className='font-bold'>{order.contract_id}</h2>
              <h3 className='text-gray'>{formatCurrency(order.amount)}</h3>
            </div>
            <PaymentResumeEmpaqueRow
              payment={order}
              label={literals.empaque}
            />
          </div>
        ))}
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.date}</h2>
          <h3 className='text-neutral-500'>{formatDate(summary.date, 'DD/MM/YYYY')}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.paymentMethod2}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.mercadopago}</h3>
        </div>
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.status}</h2>
          <h3 className='text-neutral-500 font-bold'>{literals.statusValues[summary.status] || summary.status}</h3>
        </div>
        {summary.status === CONTRACT_STATUS.COMPLETED && (
          <PaymentSummaryBarcode reference={summary.barcodeReference} />
        )}
        <div className='separator my-4' />
        <div className='flex justify-between mb-2'>
          <h2 className='font-bold'>{literals.totalAmountPaid}</h2>
          <h3 className='text-primary font-bold'>
            {formatCurrency(summary.totalAmount)}
          </h3>
        </div>
        <Button
          className='mt-6'
          type='primary'
          text={literals.common.back}
          onClick={() => {
            setMultiplePaymentSummary(null);
            setStep(0);
            setPaymentMethod(null);
            navigate(ROUTE_PATH.PAYMENTS);
          }}
        />
      </div>
    );
  };

  const panelClassName = 'multiple-payment__panel rounded-card bg-white p-4 shadow-card lg:p-6';

  if (isOutOfHour || multiplePaymentSummary) {
    return (
      <PaymentCheckout
        title={literals.title}
        onBack={handleBack}
        showStepper={false}
        summary={checkoutSummary}
      >
        <div className={`multiple-payment ${panelClassName}`}>
          {isOutOfHour
            ? <PaymentOutOfHour schedule={paymentsSchedule} />
            : renderMultiplePaymentSummary()}
        </div>
      </PaymentCheckout>
    );
  }

  return (
    <PaymentCheckout
      title={steps[step]?.label || literals.title}
      onBack={handleBack}
      steps={steps}
      currentStep={step}
      summary={checkoutSummary}
    >
      <div className='multiple-payment'>
        {/* Each contract is already its own card, so step 0 needs no outer panel. */}
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && (
          // Mercado Pago already renders its own card; wrapping it again doubles the frame.
          <div className={paymentMethod === PROVIDER_TYPE.MERCADO_PAGO ? '' : panelClassName}>
            <Suspense fallback={<p className='m-0 text-neutral-500'>{checkoutLiterals.loadingMethod}</p>}>
              {renderStep2()}
            </Suspense>
          </div>
        )}
      </div>
    </PaymentCheckout>
  );
};

export default MultiplePayment;
